import React, { useState } from "react";
import axios from "axios";
import { Mail, Send, CheckCircle2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-white border border-[#D2E0E6] text-[#0B2830] text-sm px-3 py-2.5 rounded-md focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [hp, setHp] = useState("");
  const [captcha] = useState(() => ({ a: Math.floor(Math.random() * 8) + 1, b: Math.floor(Math.random() * 8) + 1 }));
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      setError("Please fill in your name, a valid email, and a message.");
      return;
    }
    if (parseInt(answer, 10) !== captcha.a + captcha.b) {
      setError("Please answer the anti-bot question correctly.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, { ...form, hp });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-xl mx-auto fade-up" data-testid="contact-page">
      <img src="/qls-lockup.png" alt="Quantum Leap Solutions" data-testid="contact-logo"
        className="h-10 md:h-12 w-auto mb-3 object-contain" />
      <div className="flex items-center gap-1.5 text-[#47616B] mb-6" data-testid="contact-location">
        <MapPin className="w-4 h-4 text-[#0D9488]" />
        <span className="text-sm font-medium">Nashville, TN</span>
      </div>
      <div className="flex items-center gap-2 text-[#0284C7] mb-1">
        <Mail className="w-5 h-5" />
        <span className="mono text-xs uppercase tracking-[0.25em]">Get in touch</span>
      </div>
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0B2830] mb-2">Contact us</h1>
      <p className="text-sm text-[#47616B] mb-6">
        Have a question or want to work with us? Send a message and we'll get back to you.
      </p>

      <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-6">
        {sent ? (
          <div className="text-center py-8 space-y-3" data-testid="contact-success">
            <CheckCircle2 className="w-12 h-12 text-[#0D9488] mx-auto" />
            <h3 className="text-lg font-semibold text-[#0B2830]">Message sent</h3>
            <p className="text-sm text-[#47616B]">
              Thank you for your message. We will get back to you very soon.
            </p>
            <button data-testid="contact-again" onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }}
              className="mt-2 text-sm text-[#0284C7] hover:underline">Send another message</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">Your name</label>
              <input data-testid="contact-name" value={form.name} onChange={(e) => upd("name", e.target.value)}
                className={inputCls} placeholder="Jane Doe" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">Email address</label>
              <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)}
                className={inputCls} placeholder="jane@example.com" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">Message</label>
              <textarea data-testid="contact-message" value={form.message} onChange={(e) => upd("message", e.target.value)}
                rows={5} className={inputCls + " resize-y"} placeholder="How can we help?" />
            </div>
            {/* Honeypot: hidden from humans, bots tend to fill it */}
            <input type="text" name="company_website" value={hp} onChange={(e) => setHp(e.target.value)}
              tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">
                Anti-bot check: what is {captcha.a} + {captcha.b}?
              </label>
              <input data-testid="contact-captcha" type="text" inputMode="numeric" value={answer}
                onChange={(e) => setAnswer(e.target.value)} className={inputCls} placeholder="Answer" />
            </div>
            {error && (
              <div data-testid="contact-error" className="text-sm text-[#E11D48] border border-[#E11D48]/30 bg-[#E11D48]/5 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <button data-testid="contact-submit" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[#0284C7] text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
